import { GoogleReCaptchaProvider, type GoogleReCaptchaProviderProps } from '@google-recaptcha/react';
import {
  createContext,
  memo,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { reportErrorToBugsnag } from '@air/utils-error';

import { ONE_MINUTE, ONE_SECOND } from '~/constants/unitsOfTime';
import { isDevOrTestStage } from '~/swr-hooks/utils';

const RECAPTCHA_TIMEOUT_MINUTES = 3;
const EXECUTE_TIMEOUT_MS = RECAPTCHA_TIMEOUT_MINUTES * ONE_MINUTE;

type RecaptchaInstance = Parameters<NonNullable<GoogleReCaptchaProviderProps['onLoad']>>[0];

type ReCaptchaContextValue = {
  executeRecaptcha: () => Promise<string>;
  isReady: boolean;
  isLoading: boolean;
};

const DEFAULT_CONTEXT_VALUE: ReCaptchaContextValue = {
  executeRecaptcha: async () => '',
  isLoading: false,
  isReady: false,
};

const ReCaptchaContext = createContext(DEFAULT_CONTEXT_VALUE);

export type ReCaptchaProviderProps = {
  children: ReactNode;
  siteKey: string | undefined;
};

type PendingRecaptcha = {
  resolve: (token: string) => void;
  reject: (error: Error) => void;
};

export const ReCaptchaProvider = memo(({ children, siteKey }: ReCaptchaProviderProps) => {
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const instanceRef = useRef<RecaptchaInstance | null>(null);
  const pendingRef = useRef<PendingRecaptcha | null>(null);
  const recaptchaTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleLoad = useCallback((instance: RecaptchaInstance) => {
    instanceRef.current = instance;
    setIsLoading(false);
  }, []);

  useEffect(() => {
    return () => {
      if (recaptchaTimeoutRef.current) {
        clearTimeout(recaptchaTimeoutRef.current);
      }
    };
  }, []);

  const rejectAndCleanup = useCallback((error: Error) => {
    if (recaptchaTimeoutRef.current) {
      clearTimeout(recaptchaTimeoutRef.current);
    }
    recaptchaTimeoutRef.current = null;
    pendingRef.current?.reject(error);
    pendingRef.current = null;
    instanceRef.current?.reset?.();
  }, []);

  const handleCallback = useCallback((token: string) => {
    if (recaptchaTimeoutRef.current) {
      clearTimeout(recaptchaTimeoutRef.current);
    }
    recaptchaTimeoutRef.current = null;
    pendingRef.current?.resolve(token);
    pendingRef.current = null;
    instanceRef.current?.reset?.();
  }, []);

  const handleError = useCallback(() => {
    rejectAndCleanup(new Error('reCAPTCHA error'));
  }, [rejectAndCleanup]);

  const handleExpired = useCallback(() => {
    rejectAndCleanup(new Error('reCAPTCHA token expired'));
  }, [rejectAndCleanup]);

  const executeRecaptcha = useCallback(
    () =>
      new Promise<string>((resolve, reject) => {
        if (pendingRef.current) {
          rejectAndCleanup(new Error('reCAPTCHA cancelled by new execution'));
        }
        pendingRef.current = { resolve, reject };
        recaptchaTimeoutRef.current = setTimeout(() => {
          rejectAndCleanup(
            new Error(`reCAPTCHA timeout - no token returned within ${EXECUTE_TIMEOUT_MS / ONE_SECOND} seconds`),
          );
        }, EXECUTE_TIMEOUT_MS);
        instanceRef.current?.execute?.();
      }),
    [rejectAndCleanup],
  );

  const explicit = useMemo<GoogleReCaptchaProviderProps['explicit']>(
    () => ({
      container: containerEl ?? undefined,
      callback: handleCallback,
      errorCallback: handleError,
      expiredCallback: handleExpired,
      badge: 'hidden',
    }),
    [containerEl, handleCallback, handleError, handleExpired],
  );

  const value = useMemo<ReCaptchaContextValue>(
    () => ({ executeRecaptcha, isReady: !isLoading, isLoading }),
    [executeRecaptcha, isLoading],
  );

  if (!siteKey) {
    return <ReCaptchaContext.Provider value={value}>{children}</ReCaptchaContext.Provider>;
  }

  return (
    <ReCaptchaContext.Provider value={value}>
      <div ref={setContainerEl} className="fixed -top-[9999px] -left-[9999px]" />
      {containerEl && (
        <GoogleReCaptchaProvider type="v2-invisible" siteKey={siteKey} explicit={explicit} onLoad={handleLoad} />
      )}
      {children}
    </ReCaptchaContext.Provider>
  );
});

ReCaptchaProvider.displayName = 'ReCaptchaProvider';

export const useReCaptcha = (): ReCaptchaContextValue => {
  const context = useContext(ReCaptchaContext);

  if (context === DEFAULT_CONTEXT_VALUE) {
    const error = new Error('useReCaptcha used outside of ReCaptchaProvider');

    if (isDevOrTestStage()) {
      throw error;
    } else {
      reportErrorToBugsnag({ error, context: error.message });
    }
  }

  return context;
};
